import { forwardRef, useState, useCallback, useEffect } from 'react';
import { HiArrowLeft } from 'react-icons/hi';
import { FaSave } from 'react-icons/fa';
import { HiChevronLeft, HiChevronRight } from 'react-icons/hi';

import { Modal, ModalMethods, Text, toasts, Loader } from '../../../../../ui-component';
import { api } from '../../../../../utils/apiRequest';
import { getValueFromLocalStorage } from '../../../../../utils/common';

const SIZE_LABELS = ['XS', 'S', 'M', 'L', 'XL', 'XXL'] as const;

type MeasurementParam = {
  id: number;
  name: string;
  icon_url?: string;
};

type BoutiqueMeasurementsResponse = {
  id: number;
  params: MeasurementParam[];
  atomic_outfit_response?: {
    id: number;
    name: string;
    gender?: string;
  };
  boutique_id: number;
  measurement_scale?: {
    scale: string;
  };
};

type SizeChartSizes = {
  XS: number;
  S: number;
  M: number;
  L: number;
  XL: number;
  XXL: number;
};

type SizeCharts = {
  [paramName: string]: SizeChartSizes;
};

type OutfitPiece = {
  id: number;
  name: string;
  outfit_link?: string;
  icon_reference_id?: string;
};

type SizeChartModalProps = {
  outfitName: string;
  outfitPieces: OutfitPiece[];
  selectedPieceId: number | null;
  onPieceChange?: (pieceId: number) => void;
  onSaved?: () => void;
};

const getEmptySizes = (): SizeChartSizes => ({
  XS: 0,
  S: 0,
  M: 0,
  L: 0,
  XL: 0,
  XXL: 0,
});

const formatMeasurementName = (name: string) => {
  return name
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const SizeChartModal = forwardRef<ModalMethods, SizeChartModalProps>(
  ({ outfitPieces, selectedPieceId, onPieceChange, onSaved }, ref) => {
    const [measurementsData, setMeasurementsData] = useState<BoutiqueMeasurementsResponse | null>(
      null
    );
    const [sizeChartsData, setSizeChartsData] = useState<SizeCharts | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const currentPiece = outfitPieces.find((p) => p.id === selectedPieceId);

    const fetchMeasurements = useCallback(async () => {
      const boutique_id = getValueFromLocalStorage('boutique_id');

      if (!boutique_id || !selectedPieceId) {
        setMeasurementsData(null);
        setSizeChartsData(null);

        return;
      }

      setIsLoading(true);

      try {
        const [measurementsResponse, detailsResponse] = await Promise.all([
          api.getRequest(
            `new_measurement/v2/${boutique_id}/boutique_measurements?atomic_outfit_id=${selectedPieceId}`
          ),
          api.getRequest(
            `outfit_piece/fetch/all_details?boutique_id=${boutique_id}&atomic_outfit_id=${selectedPieceId}`
          ),
        ]);

        if (measurementsResponse.status && measurementsResponse.data) {
          setMeasurementsData(measurementsResponse.data);
        } else {
          setMeasurementsData(null);
        }

        const detailsSizeCharts = detailsResponse?.data?.sizeCharts ?? null;

        if (measurementsResponse.status && measurementsResponse.data) {
          const params = measurementsResponse.data.params ?? [];
          const merged: SizeCharts = {};

          params.forEach((param: MeasurementParam) => {
            const existing = detailsSizeCharts?.[param.name];

            merged[param.name] = {
              XS: existing?.XS ?? 0,
              S: existing?.S ?? 0,
              M: existing?.M ?? 0,
              L: existing?.L ?? 0,
              XL: existing?.XL ?? 0,
              XXL: existing?.XXL ?? 0,
            };
          });

          setSizeChartsData(merged);
        } else {
          setSizeChartsData(detailsSizeCharts);
        }
      } catch (err) {
        if (err instanceof Error) {
          toasts('error', err.message, 'size-chart-fetch-error');
        }
        setMeasurementsData(null);
        setSizeChartsData(null);
      } finally {
        setIsLoading(false);
      }
    }, [selectedPieceId]);

    useEffect(() => {
      void fetchMeasurements();
    }, [fetchMeasurements]);

    const handleSizeChange = (
      paramName: string,
      size: (typeof SIZE_LABELS)[number],
      value: string
    ) => {
      const num = Number(value.replace(/[^0-9.]/g, '')) || 0;

      setSizeChartsData((prev) => {
        const next = { ...prev };
        const paramSizes = { ...(next[paramName] ?? getEmptySizes()) };

        paramSizes[size] = num;
        next[paramName] = paramSizes;

        return next;
      });
    };

    const handleSave = async () => {
      const boutique_id = getValueFromLocalStorage('boutique_id');

      if (!boutique_id || !selectedPieceId || !measurementsData) {
        toasts('error', 'Missing required data', 'size-chart-save-error');

        return;
      }

      const params = measurementsData.params ?? [];

      if (params.length === 0) {
        toasts('error', 'No measurement parameters to save', 'size-chart-save-error');

        return;
      }

      setIsSaving(true);

      try {
        let successCount = 0;

        for (const param of params) {
          const sizes = sizeChartsData?.[param.name] ?? getEmptySizes();

          const payload = {
            boutiqueId: Number(boutique_id),
            atomicOutfitId: selectedPieceId,
            paramName: param.name,
            sizes: {
              XS: sizes.XS,
              S: sizes.S,
              M: sizes.M,
              L: sizes.L,
              XL: sizes.XL,
              XXL: sizes.XXL,
            },
          };

          const response = await api.postRequest('custom_size_chart/save', payload);

          if (response.status) {
            successCount += 1;
          }
        }

        if (successCount > 0) {
          toasts('success', 'Size chart saved successfully', 'size-chart-save-success');

          if (onSaved) {
            onSaved();
          }

          if (typeof ref === 'object' && ref !== null && 'current' in ref && ref.current) {
            ref.current.hide();
          }
        } else {
          toasts('error', 'Failed to save size chart', 'size-chart-save-error');
        }
      } catch (err) {
        if (err instanceof Error) {
          toasts('error', err.message, 'size-chart-save-error');
        }
      } finally {
        setIsSaving(false);
      }
    };

    const handleNavigatePiece = (direction: 'prev' | 'next') => {
      const currentIndex = outfitPieces.findIndex((p) => p.id === selectedPieceId);

      if (currentIndex === -1) {
        return;
      }

      const newIndex = direction === 'prev' ? currentIndex - 1 : currentIndex + 1;

      if (newIndex >= 0 && newIndex < outfitPieces.length) {
        const newPiece = outfitPieces[newIndex];

        if (onPieceChange) {
          onPieceChange(newPiece.id);
        }
      }
    };

    const handleBack = () => {
      if (typeof ref === 'object' && ref !== null && 'current' in ref && ref.current) {
        ref.current.hide();
      }
    };

    const unit = measurementsData?.measurement_scale?.scale ?? 'inch';
    const params = measurementsData?.params ?? [];

    const headerComponent = (
      <div className="flex items-center justify-between w-full pr-8">
        <button
          type="button"
          onClick={handleBack}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors"
        >
          <HiArrowLeft size={20} />
          <Text size="small" color="black" className="font-inter">
            Back
          </Text>
        </button>
        <Text size="large" fontWeight={700} className="font-inter">
          Standard size chart
        </Text>
        <button
          type="button"
          onClick={handleSave}
          disabled={isLoading || isSaving || params.length === 0}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <FaSave size={14} />
          <span>Save</span>
        </button>
      </div>
    );

    return (
      <Modal
        ref={ref}
        size="xl"
        headerComponent={headerComponent}
        showFooter={false}
        showCloseIcon={false}
      >
        <div className="flex flex-col h-full min-h-[400px]">
          <Text
            className="font-inter text-sm text-gray-600 mb-4  mt-1 flex items-center"
            color="black"
          >
            Define what XS/S/M/L/XL means for your business. Works for any category. Values use
            global unit ({unit}).
          </Text>

          {outfitPieces.length > 0 && (
            <div className="flex items-center gap-2 mb-6">
              <button
                type="button"
                onClick={() => handleNavigatePiece('prev')}
                disabled={outfitPieces.findIndex((p) => p.id === selectedPieceId) <= 0}
                className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center hover:bg-gray-300 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <HiChevronLeft size={18} />
              </button>
              <button
                type="button"
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-gray-100 hover:bg-gray-200 min-w-[300px]"
              >
                {currentPiece?.outfit_link ? (
                  <img
                    src={currentPiece.outfit_link}
                    alt={currentPiece.name}
                    className="w-8 h-8 rounded-lg object-cover"
                  />
                ) : (
                  <span className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center text-gray-500 text-xs">
                    +
                  </span>
                )}
                <Text className="font-inter text-sm truncate" color="black">
                  {currentPiece?.name ?? 'Select piece'}
                </Text>
              </button>
              <button
                type="button"
                onClick={() => handleNavigatePiece('next')}
                disabled={
                  outfitPieces.findIndex((p) => p.id === selectedPieceId) >= outfitPieces.length - 1
                }
                className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center hover:bg-gray-300 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <HiChevronRight size={18} />
              </button>
            </div>
          )}

          <div className="flex-1 overflow-y-auto pr-2 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-track]:rounded [&::-webkit-scrollbar-thumb]:bg-gray-400 [&::-webkit-scrollbar-thumb]:rounded [&::-webkit-scrollbar-thumb]:hover:bg-gray-500">
            {isLoading ? (
              <div className="flex justify-center items-center h-[200px]">
                <Loader />
              </div>
            ) : params.length > 0 ? (
              <div className="space-y-6 pb-5 ">
                {params.map((param) => {
                  const sizes = sizeChartsData?.[param.name] ?? getEmptySizes();

                  return (
                    <div
                      key={param.id}
                      className="space-y-3 border-2 border-gray-200 rounded-lg p-4 shadow-mg"
                    >
                      <Text className="font-inter font-semibold text-sm" color="black">
                        {formatMeasurementName(param.name)}
                      </Text>
                      <div className="grid grid-cols-3 gap-3">
                        {SIZE_LABELS.map((sizeLabel) => (
                          <div key={sizeLabel} className="flex flex-col gap-1">
                            <label
                              htmlFor={`${param.id}-${sizeLabel}`}
                              className="text-xs text-gray-500 font-inter"
                            >
                              {sizeLabel}
                            </label>
                            <input
                              id={`${param.id}-${sizeLabel}`}
                              type="text"
                              inputMode="decimal"
                              value={sizes[sizeLabel] !== undefined ? String(sizes[sizeLabel]) : ''}
                              onChange={(e) =>
                                handleSizeChange(param.name, sizeLabel, e.target.value)
                              }
                              placeholder={`${sizeLabel}--`}
                              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-inter focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-[200px] p-5">
                <Text color="gray" size="small" className="font-inter">
                  No measurements configured. Add measurements first to define size chart.
                </Text>
              </div>
            )}
          </div>
        </div>
      </Modal>
    );
  }
);

SizeChartModal.displayName = 'SizeChartModal';

export default SizeChartModal;
